import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Mutation, Query} from 'react-apollo';
import {Button, Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {PulseLoader} from 'react-spinners';
import styled from 'styled-components';
import config from '../../../config/app';
import {checkErrorType} from '../../../utils';
import {validateEmail} from '../../../../modules/utils';
import Error from '../../../common/components/Error/Error';
import Image from '../../../common/components/Image';
import MandatoryInput from '../../../common/components/layout/MandatoryInput';
import PopIn from '../../../common/components/PopIn';
import SelectBrand from '../../../common/components/SelectBrand';
import {CancelButton, FooterRow, GradientCol, TitleButton} from '../../../common/components/StyledComp';
import Tooltip from '../../../common/components/Tooltip';
import {ERROR_TYPE_EMAIL_ALREADY_USED} from '../../../common/constants';

const GRAPHQL_QUERY_BRAND = gql`
  query brand($id: ID!){
    brand(id: $id){
      id
      name
      createdAt
      isActive
      file {
        id
        token
        filename
      }
    }
  }
`;

export const GRAPHQL_MUTATION_REGISTER_CENTRAL_PURCHASING = gql`
  mutation registerCentralPurchasing($userInput: RegisterUserInput!, $centralPurchasingInput: RegisterCentralPurchasingInput!) {
    registerCentralPurchasing(userInput: $userInput, centralPurchasingInput: $centralPurchasingInput) {
      id
      firstName
      lastName
      isActive
      isDeleted
      centralPurchasing{
        id
        street
        postalCode
        city
        contactEmail
        siret
        createdAt
        updatedAt
        brand{
          id
          name
        }
      }
    }
  }
`;


const Div = styled.div`
  text-transform: uppercase;
  font-family: 'Segoe UI Bold', sans-serif;
  font-size: 12px;
`;

const HeadDiv = styled.div`
  text-transform: uppercase;
  font-family: 'Segoe UI', sans-serif;
  font-size: 12px;
`;

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  height: auto;
  width: auto;
`;


const AddCentralPurchasing = props => {
  const {t} = props;

  const initialCentralPurchasing = {
    brandId: '',
    street: '',
    postalCode: '',
    city: '',
    contactEmail: '',
    siret: '',
    contactName: '',
  };

  const initialUser = {
    firstName: '',
    lastName: '',
    email: '',
  };

  const [showModal, setShowModal] = useState(false);
  const [centralPurchasing, setCentralPurchasing] = useState(initialCentralPurchasing);
  const [user, setUser] = useState(initialUser);
  const [submitted, setSubmitted] = useState(false);

  const submitChanges = mutation => {
    setSubmitted(true);
    if (
      user.firstName !== '' &&
      user.lastName !== '' &&
      user.email !== '' &&
      validateEmail(user.email) &&
      centralPurchasing.street !== '' &&
      centralPurchasing.postalCode !== '' &&
      centralPurchasing.city !== '' &&
      centralPurchasing.contactEmail !== '' &&
      validateEmail(centralPurchasing.contactEmail) &&
      centralPurchasing.siret !== '' &&
      centralPurchasing.brandId !== '' &&
      centralPurchasing.contactName !== ''
    ) {
      mutation({variables: {userInput: user, centralPurchasingInput: centralPurchasing}});
    }
  };

  return (
    <div>
      <Button onClick={() => setShowModal(true)} className="title-btn">
        {t('AddCentralPurchasing.createNew')}
      </Button>
      <Mutation
        mutation={GRAPHQL_MUTATION_REGISTER_CENTRAL_PURCHASING}
        variables={{user,centralPurchasing}}
        onCompleted={() => {
          setShowModal(false);
          setUser(initialUser);
          setCentralPurchasing(initialCentralPurchasing);
          setSubmitted(false);
        }}
        refetchQueries={['centralsPurchasing']}
        awaitRefetchQueries
      >
        {(mutation, {error, loading}) => {
          const existingEmail = checkErrorType(error, ERROR_TYPE_EMAIL_ALREADY_USED);
          if (error && !existingEmail) {
            console.error(error);
          }
          return (
            <PopIn title={t('AddCentralPurchasing.createNew')} visible={showModal} setModalVisible={setShowModal} size={'xl'}>
              <form>
                <Container className="general-container">
                  <Row className="info-row">
                    <Col xs={6}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('AddCentralPurchasing.personalInfos')} : `}</HeadDiv>
                        <MandatoryInput
                          title={`${t('lastName')} : `}
                          name="lastName"
                          onChange={(name, e) => setUser({...user, lastName: e})}
                          value={user.lastName}
                          show={submitted && user.lastName === ''}
                        />
                        <MandatoryInput
                          title={`${t('firstName')} : `}
                          name="firstName"
                          onChange={(name, e) => setUser({...user, firstName: e})}
                          value={user.firstName}
                          show={submitted && user.firstName === ''}
                        />
                        <MandatoryInput
                          title={`${t('personalEmail')} : `}
                          type="email"
                          name="email"
                          onChange={(name, e) => {
                            setUser({...user, email: e});
                          }}
                          value={user.email}
                          show={submitted && (user.email === '' || !validateEmail(user.email) || existingEmail)}
                          customText={existingEmail ? t('existingEmail') : t('wrongMail')}
                        />
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('brandInfo')} : `}</HeadDiv>
                        <Col xs={12} className="brand-info">
                          <label className="brand-label">
                            <Row className="brand-info table-container">
                              <Col xs={9} className="extended-col">
                                <label>
                                  <Div className="attribute-name">{`${t('brand')} : `}</Div>
                                  <label>
                                    <SelectBrand
                                      onChange={option => setCentralPurchasing({...centralPurchasing, brandId: Number(option.value)})}
                                      empty={submitted && centralPurchasing.brandId === ''}
                                    />
                                  </label>
                                  <Tooltip show={submitted && centralPurchasing.brandId === ''} color={'red'}>
                                    {t('AddCentralPurchasing.requiredField')}
                                  </Tooltip>
                                </label>
                              </Col>
                              <Col xs={3} className="brand-logo">
                                {centralPurchasing.brandId !== '' && (
                                  <Query query={GRAPHQL_QUERY_BRAND} variables={{id: centralPurchasing.brandId}}>
                                    {({data, loading, error}) => {
                                      if (loading) {
                                        return <PulseLoader loading={true} />;
                                      }
                                      if (error) {
                                        console.error(error);
                                      }
                                      if (data && data.brand.file) {
                                        return <Image src={config.api_file_brand + data.brand.id + '?token=' + data.brand.file.token}/>;
                                      }
                                      return null;
                                    }}
                                  </Query>
                                )}
                              </Col>
                            </Row>
                          </label>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-row">
                    <Col xs={12}>
                      <Row className="section-info">
                        <HeadDiv className="info-header">{`${t('centralPurchasingInfo')} : `}</HeadDiv>
                        <MandatoryInput
                          title={`${t('SIRET')} : `}
                          size={6}
                          type="text"
                          name="siret"
                          value={centralPurchasing.siret}
                          onChange={(name, e) => {
                            if (e.length <= 14) {
                              setCentralPurchasing({...centralPurchasing, siret: e});
                            }
                          }}
                          show={submitted && centralPurchasing.siret === ''}
                        />
                        <MandatoryInput
                          title={`${t('street')} : `}
                          name="street"
                          size={6}
                          onChange={(name, e) => {
                            setCentralPurchasing({...centralPurchasing, street: e});
                          }}
                          value={centralPurchasing.street}
                          show={submitted && centralPurchasing.street === ''}
                        />
                        <Col xs={6}>
                          <Row>
                            <MandatoryInput
                              title={`${t('postal')} : `}
                              size={3}
                              type="number"
                              name="postalCode"
                              onChange={(name, e) => setCentralPurchasing({...centralPurchasing, postalCode: e})}
                              value={centralPurchasing.postalCode}
                              show={submitted && centralPurchasing.postalCode === ''}
                            />
                            <MandatoryInput
                              title={`${t('city')} : `}
                              size={9}
                              name="city"
                              onChange={(name, e) => {
                                setCentralPurchasing({...centralPurchasing, city: e});
                              }}
                              value={centralPurchasing.city}
                              show={submitted && centralPurchasing.city === ''}
                            />
                          </Row>
                        </Col>
                        <MandatoryInput
                          title={`${t('contactEmail')} : `}
                          type="email"
                          size={6}
                          name="contactEmail"
                          onChange={(name, e) => {
                            setCentralPurchasing({...centralPurchasing, contactEmail: e});
                          }}
                          value={centralPurchasing.contactEmail}
                          show={submitted && (centralPurchasing.contactEmail === '' || !validateEmail(centralPurchasing.contactEmail))}
                          customText={t('wrongMail')}
                        />
                        <MandatoryInput
                          title={`${t('contactName')} : `}
                          size={6}
                          name="contactName"
                          onChange={(name, e) => {
                            setCentralPurchasing({...centralPurchasing, contactName: e});
                          }}
                          value={centralPurchasing.contactName}
                          show={submitted && (centralPurchasing.contactName === '')}
                        />
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </form>
              <FooterRow>
                <GradientCol sm={{span: 6, offset: 3}}>
                  {error && !existingEmail && (
                    <Error error={t('unknownError')}/>
                  )}
                  <div className="popin-buttons">
                    <div>
                      <TitleButton disabled={loading} onClick={() => submitChanges(mutation)}>
                        {t('AddCentralPurchasing.submit')}
                      </TitleButton>
                    </div>
                    <CancelButtonForm
                      variant="secondary"
                      onClick={() => {
                        setShowModal(false);
                        setCentralPurchasing(initialCentralPurchasing);
                        setSubmitted(false);
                      }}>
                      {t('AddCentralPurchasing.close')}
                    </CancelButtonForm>
                  </div>
                </GradientCol>
              </FooterRow>
            </PopIn>
          );
        }}
      </Mutation>
    </div>
  );
};

AddCentralPurchasing.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('centralPurchasing')(AddCentralPurchasing);
